import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSocial } from "../../contexts/SocialContext";
import { CLASSES } from "../../data/classes";
import { BORDERS_BY_ID } from "../../data/cosmetics";
import { getRank } from "../../data/achievements";
import { X, Crown, Sparkles, Trophy, Loader2, Circle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfileModal = () => {
  const { profileUserId, closeProfile, isOnline } = useSocial();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!profileUserId) { setData(null); setError(false); return; }
    let mounted = true;
    setLoading(true); setError(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/profile/${profileUserId}`, { withCredentials: true });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [profileUserId]);

  if (!profileUserId) return null;

  const cls = data?.avatar_classe ? CLASSES[data.avatar_classe] : null;
  const border = BORDERS_BY_ID[data?.equipped_border || "default"] || BORDERS_BY_ID.default;
  const rankInfo = data ? getRank(data.rank_points || 0) : null;
  const online = isOnline(profileUserId);

  return (
    <div data-testid="profile-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={closeProfile}>
      <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-6 cartoon-shadow-lg-static max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="profile-modal-close" onClick={closeProfile}
          className="absolute -top-2 -right-2 bg-rose-500 text-white border-4 border-slate-950 rounded-full w-9 h-9 flex items-center justify-center cartoon-shadow z-10">
          <X className="w-4 h-4" />
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 gap-2"><Loader2 className="w-6 h-6 animate-spin" /> Chargement…</div>
        ) : error || !data ? (
          <p className="text-center text-slate-400 py-12">Profil introuvable.</p>
        ) : (
          <>
            <div className="flex flex-col items-center text-center">
              <div className={`w-24 h-24 rounded-2xl overflow-hidden ${border.style.className} mb-3`}>
                {cls ? (
                  <div className={`w-full h-full bg-gradient-to-br ${cls.couleur}`}>
                    <img src={cls.image} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : data.picture ? (
                  <img src={data.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-700 flex items-center justify-center"><Sparkles className="w-8 h-8 text-slate-500" /></div>
                )}
              </div>
              <h2 data-testid="profile-modal-name" className="font-display text-2xl font-bold flex items-center gap-2">
                {data.display_name || data.name}
                <Circle className={`w-3 h-3 ${online ? "text-emerald-400 fill-emerald-400" : "text-slate-600 fill-slate-600"}`} />
              </h2>
              <p className="text-xs text-slate-400">{online ? "En ligne" : "Hors ligne"}{data.friend_code ? ` · Code : ${data.friend_code}` : ""}</p>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-5">
              <div className="bg-slate-900 border-2 border-slate-950 rounded-xl p-2 text-center">
                <Sparkles className="w-4 h-4 mx-auto text-fuchsia-400 mb-1" />
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Niveau</p>
                <p data-testid="profile-modal-level" className="font-display font-bold text-lg">{data.level || 1}</p>
              </div>
              <div className="bg-slate-900 border-2 border-slate-950 rounded-xl p-2 text-center">
                <Crown className="w-4 h-4 mx-auto text-yellow-400 mb-1" />
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Rang PvP</p>
                <p className="font-display font-bold text-sm leading-tight">{rankInfo?.current?.label || "—"}</p>
                <p className="text-[10px] text-yellow-400 font-bold">{data.rank_points || 0} pts</p>
              </div>
              <div className="bg-slate-900 border-2 border-slate-950 rounded-xl p-2 text-center">
                <Trophy className="w-4 h-4 mx-auto text-amber-400 mb-1" />
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Succès</p>
                <p className="font-display font-bold text-lg">{data.achievements_count || 0}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
